import {match} from 'ts-pattern';

import {ReactNode, useEffect, useMemo} from 'react';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {ConfirmDialog} from '../../ConfirmDialog/ConfirmDialog';
import {DeleteDialog} from '../../DeleteDialog/DeleteDialog';
import {Portal} from '../../Portal/components/Portal';
import {PortalManager} from '../../Portal/components/PortalManager';
import {SuccessDialog} from '../../SuccessDialog/SuccessDialog';
import {Dialog} from '../Dialog';
import {useDialogsState} from '../hooks/useDialogsState';
import {DIALOGS_EVENTS, DialogEventType} from '../utils/dialogsEvents';
import {DialogsContext} from './DialogsContext';

export interface DialogsProviderProps {
  children: ReactNode;
}

export function DialogsProvider(props: DialogsProviderProps) {
  const theme = useComponentsTheme();
  const {
    dialogs,
    openDialog,
    closeDialog,
    openDeleteDialog,
    closeCurrentDialog,
    openConfirmDialog,
    openSuccessDialog,
  } = useDialogsState();

  const events = useMemo<Record<DialogEventType, EventListener>>(
    () => ({
      openDelete: (event: Event) => {
        isCustomEvent(event) && openDeleteDialog(event.detail.options);
      },
      openConfirm: (event: Event) => {
        isCustomEvent(event) && openConfirmDialog(event.detail.options);
      },
      openSuccess: (event: Event) => {
        isCustomEvent(event) && openSuccessDialog(event.detail.options);
      },
      open: (event: Event) => {
        isCustomEvent(event) && openDialog(event.detail.content, event.detail.options);
      },
      close: (event: Event) => {
        isCustomEvent(event) && closeDialog(event.detail.id);
      },
      closeCurrent: closeCurrentDialog,
    }),
    [
      openDialog,
      openDeleteDialog,
      openConfirmDialog,
      closeDialog,
      closeCurrentDialog,
      openSuccessDialog,
    ]
  );

  useEffect(
    function subscribeToDialogEvents() {
      const eventListeners: Array<[string, EventListener]> = [
        [DIALOGS_EVENTS.openDelete, events.openDelete],
        [DIALOGS_EVENTS.openConfirm, events.openConfirm],
        [DIALOGS_EVENTS.openSuccess, events.openSuccess],
        [DIALOGS_EVENTS.open, events.open],
        [DIALOGS_EVENTS.close, events.close],
        [DIALOGS_EVENTS.closeCurrent, events.closeCurrent],
      ];

      eventListeners.forEach(([eventName, listener]) =>
        window.addEventListener(eventName, listener)
      );
      return () => {
        eventListeners.forEach(([eventName, listener]) =>
          window.removeEventListener(eventName, listener)
        );
      };
    },
    [events]
  );

  const isAnyDialogOpen = dialogs.some((dialog) => dialog.props.isOpen);

  return (
    <>
      <PortalManager zIndex={theme.zIndices.FULLSCREEN_MODAL}>
        <Portal>
          {dialogs.map((dialog) =>
            match(dialog)
              .with({type: 'dialog'}, ({props: dialogProps}) => (
                <Dialog {...dialogProps} key={dialogProps.id} />
              ))
              .with({type: 'confirm'}, ({props: dialogProps}) => (
                <ConfirmDialog {...dialogProps} key={dialogProps.id} />
              ))
              .with({type: 'delete'}, ({props: dialogProps}) => (
                <DeleteDialog {...dialogProps} key={dialogProps.id} />
              ))
              .with({type: 'success'}, ({props: dialogProps}) => (
                <SuccessDialog {...dialogProps} key={dialogProps.id} />
              ))
              .exhaustive()
          )}
        </Portal>
      </PortalManager>
      <DialogsContext.Provider value={{isAnyDialogOpen}}>{props.children}</DialogsContext.Provider>
    </>
  );
}

const isCustomEvent = (event: Event): event is CustomEvent => 'detail' in event;
